import React, { useEffect, useRef, useState } from 'react';
import { FaSliders } from 'react-icons/fa6'; // Importar ícono para el botón EQ

interface AudioWaveformProps {
  audioTrack?: MediaStreamTrack | null;
  isAudioMuted?: boolean;
  isOwner?: boolean; // Añadir propiedad para determinar si es propietario
  onEqButtonClick?: () => void; // Callback para manejar el clic en el botón EQ
}

const AudioWaveform: React.FC<AudioWaveformProps> = ({
  audioTrack,
  isAudioMuted = false,
  isOwner = false, // Por defecto no es propietario
  onEqButtonClick
}) => {
  // Usar 16 barras para una visualización más larga
  const [audioLevels, setAudioLevels] = useState<number[]>(Array(16).fill(0));
  
  // Referencias para el contexto de audio y analizador
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const animationRef = useRef<number | null>(null);
  const isActiveRef = useRef<boolean>(false);
  
  // Referencias para el sistema mejorado de noise gate
  const noiseFloorRef = useRef<number[]>(Array(16).fill(0.02)); // Nivel de ruido adaptativo por banda
  const holdCounterRef = useRef<number>(0);
  const lastActiveTimeRef = useRef<number>(0);

  // Configurar análisis de audio cuando se recibe una pista de audio
  useEffect(() => {
    console.log("AudioWaveform useEffect running - audioTrack changed or mute state changed", 
                { hasTrack: !!audioTrack, isMuted: isAudioMuted });
    
    // Función de limpieza para la ejecución anterior del efecto
    const cleanupPreviousEffect = () => {
      console.log("Cleaning up previous audio resources");
      
      // Cancelar animación si está activa
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      
      // Desconectar fuente si existe
      if (sourceRef.current) {
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }
      
      // Desconectar analizador si existe
      if (analyserRef.current) {
        analyserRef.current.disconnect();
        analyserRef.current = null;
      }
      
      // Cerrar AudioContext si existe Y si no está ya cerrado
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        try {
          audioContextRef.current.close();
        } catch (error) {
          console.warn("Error al cerrar AudioContext:", error);
        }
      }
      audioContextRef.current = null;
      
      // Marcar como inactivo
      isActiveRef.current = false;
    };

    // Limpiar instancias previas
    cleanupPreviousEffect();

    // Si no hay pista de audio o está silenciada, no inicializar
    if (!audioTrack || isAudioMuted) {
      setAudioLevels(Array(16).fill(0));
      return;
    }

    try {
      console.log("Initializing audio analysis");
      
      // Crear el contexto de audio y el analizador
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      
      // Crear un stream solo con la pista de audio
      const audioStream = new MediaStream([audioTrack]);
      
      // Conectar la fuente al analizador
      const source = audioContext.createMediaStreamSource(audioStream);
      sourceRef.current = source;
      
      const analyser = audioContext.createAnalyser();
      analyserRef.current = analyser;
      
      // Parámetros optimizados para mayor sensibilidad y mejor filtrado del ruido
      analyser.fftSize = 512; // Mayor precisión para detectar ruido de fondo
      analyser.minDecibels = -95; // Capturar niveles de audio más bajos
      analyser.maxDecibels = -10; // Mayor rango dinámico
      analyser.smoothingTimeConstant = 0.5; // Balance entre suavidad y respuesta
      
      source.connect(analyser);
      
      // Configurar el análisis de audio
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      
      // Constantes para el noise gate adaptativo
      const NOISE_THRESHOLD = 0.03; // Umbral base para ruido
      const SPEECH_THRESHOLD = 0.06; // Umbral para considerar voz/audio real
      const HOLD_FRAMES = 12; // Cuántos frames debe superar el umbral para activarse
      const DECAY_RATE = 0.95; // Qué tan rápido decaen las barras cuando no hay sonido
      const SILENCE_TIMEOUT = 200; // ms a mantener visualización después de silencio
      
      // Función para actualizar los niveles de audio
      const updateLevels = () => {
        if (!analyserRef.current || !isActiveRef.current) {
          // Evitar ejecuciones cuando no debemos estar activos
          return;
        }
        
        analyserRef.current.getByteFrequencyData(dataArray);
        
        // Análisis por bandas de frecuencia con énfasis en voz humana (300-3000 Hz)
        const newLevels = Array(16).fill(0).map((_, i) => {
          // Cada barra corresponde a un rango específico de frecuencias
          // Usar 16 bandas espaciadas de manera no lineal para capturar mejor la voz
          
          // Dar más peso a las frecuencias medias (voz humana)
          const startBin = Math.floor(Math.pow(i / 16, 1.5) * bufferLength * 0.5);
          const endBin = Math.floor(Math.pow((i + 1) / 16, 1.5) * bufferLength * 0.5);
          
          // Calcular promedio para esta banda
          let sum = 0;
          let count = 0;
          for (let j = startBin; j < endBin; j++) {
            // Aplicar ponderación de frecuencia (dar más peso a frecuencias de voz humana 300-3000 Hz)
            let weight = 1.0;
            // Frecuencias aproximadas: index * sampleRate / fftSize / 2
            const freqHz = j * audioContext.sampleRate / analyser.fftSize / 2;
            if (freqHz > 300 && freqHz < 3000) {
              weight = 1.8; // Dar más peso a frecuencias de voz
            }
            sum += dataArray[j] * weight;
            count += weight;
          }
          
          // Normalizar a 0-1
          const bandLevel = (sum / (count * 255)) || 0;
          
          // Actualizar nivel de ruido adaptativo para esta banda
          // El nivel de ruido se adapta lentamente al nivel mínimo observado
          if (bandLevel > 0 && bandLevel < noiseFloorRef.current[i] * 1.5) {
            noiseFloorRef.current[i] = noiseFloorRef.current[i] * 0.95 + bandLevel * 0.05;
          }
          
          // Aplicar noise gate: restar el nivel de ruido estimado
          let gatedLevel = Math.max(0, bandLevel - noiseFloorRef.current[i] * 1.2);
          
          // Amplificar señal después de aplicar noise gate
          gatedLevel = Math.pow(gatedLevel, 0.6) * 1.8;
          
          // Limitar a rango 0-1
          return Math.min(1, gatedLevel);
        });
        
        // Determinar si hay audio real o solo ruido
        const maxLevel = Math.max(...newLevels);
        const avgLevel = newLevels.reduce((sum, val) => sum + val, 0) / newLevels.length;
        
        // Determinar si debemos mostrar la animación
        let shouldShowAnimation = false;
        
        const now = Date.now();
        
        if (avgLevel > SPEECH_THRESHOLD) {
          // Incrementar contador si el nivel supera el umbral de voz
          holdCounterRef.current = Math.min(HOLD_FRAMES, holdCounterRef.current + 2);
          lastActiveTimeRef.current = now;
          shouldShowAnimation = true;
        } else if (avgLevel > NOISE_THRESHOLD) {
          // Incrementar contador lentamente si hay algo por encima del ruido
          holdCounterRef.current = Math.min(HOLD_FRAMES, holdCounterRef.current + 1);
          if (holdCounterRef.current >= HOLD_FRAMES / 2) {
            lastActiveTimeRef.current = now;
            shouldShowAnimation = true;
          }
        } else {
          // Decrementar contador si el nivel está por debajo del umbral
          holdCounterRef.current = Math.max(0, holdCounterRef.current - 1);
          
          // Mantener la animación por un tiempo después del último sonido
          if (now - lastActiveTimeRef.current < SILENCE_TIMEOUT) {
            shouldShowAnimation = true;
          }
        }
        
        if (shouldShowAnimation) {
          // Actualizar los niveles si debemos mostrar la animación
          setAudioLevels(prevLevels => {
            // Verificar si hay cambios significativos para evitar actualizaciones innecesarias
            const hasSignificantChange = newLevels.some((level, i) => 
              Math.abs(level - prevLevels[i]) > 0.05
            );
            return hasSignificantChange ? newLevels : prevLevels;
          });
        } else if (audioLevels.some(level => level > 0)) {
          // Reducir gradualmente los niveles cuando no hay sonido
          setAudioLevels(prev => prev.map(level => level * DECAY_RATE));
        }
        
        // Programar la próxima actualización solo si todavía estamos activos
        if (isActiveRef.current) {
          animationRef.current = requestAnimationFrame(updateLevels);
        }
      };
      
      // Iniciar el análisis
      isActiveRef.current = true;
      updateLevels();
      
    } catch (error) {
      console.error("Error al inicializar el análisis de audio:", error);
      setAudioLevels(Array(16).fill(0));
    }
    
    // Limpieza al desmontar
    return cleanupPreviousEffect;
    
  }, [audioTrack, isAudioMuted]); // SOLO estas dependencias, no incluir audioLevels

  // Si está silenciado, mostrar indicador de silencio
  if (isAudioMuted) {
    return (
      <div className="audio-waveform audio-waveform-muted">
        <div className="audio-waveform-muted-line"></div>
        {isOwner && (
          <button 
            className="eq-button" 
            onClick={onEqButtonClick}
            title="Audio Equalizer"
          >
            <FaSliders />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="audio-waveform">
      {audioLevels.map((level, i) => (
        <div
          key={i}
          className="audio-bar"
          style={{
            height: `${Math.max(3, level * 22)}px`, 
            opacity: level > 0.01 ? (0.7 + level * 0.3) : 0,
            transform: `scaleY(${level > 0.01 ? 1 : 0})`, 
          }}
        ></div>
      ))}
      
      {/* Botón de ecualizador solo para el propietario */}
      {isOwner && (
        <button 
          className="eq-button" 
          onClick={onEqButtonClick}
          title="Audio Equalizer"
        >
          <FaSliders />
        </button>
      )}
    </div>
  );
};

export default AudioWaveform;
